import { useEffect, useId, useState, type FormEvent } from "react";
import { useSSOConfig, useTestSSOConnection, useUpdateSSOConfig } from "../../../api/queries";
import { isUnauthorized } from "../../../api/errors";
import type { SSOTestConnectionResult } from "../../../api/client";
import { ErrorState, LoadingState } from "../../../components/AsyncStates";
import { Button } from "../../../components/Button";
import { CopyButton } from "../../../components/CopyButton";
import { PageHeader } from "../../../components/PageHeader";
import { useToast } from "../../../toast/ToastContext";
import { AdminGate } from "../AdminGate";
import "../../../components/Form.css";
import "./sso.css";

/** Combines issuer + client id into the key `testedPair` is compared
 * against — Enable can only be turned on when the LAST passing test covered
 * exactly the pair currently in the form (ADR-0037 §UX "test-before-enable").
 * Editing either field after a pass invalidates it until re-tested. */
function pairKey(issuer: string, clientId: string): string {
  return `${issuer.trim()} ${clientId.trim()}`;
}

function isHttpsUrl(raw: string): boolean {
  try {
    return new URL(raw).protocol === "https:";
  } catch {
    return false;
  }
}

function SSOConfigForm() {
  const issuerId = useId();
  const clientIdId = useId();
  const clientSecretId = useId();
  const enabledId = useId();
  const { pushToast } = useToast();
  const { data, isPending, isError, error, refetch } = useSSOConfig();
  const updateMutation = useUpdateSSOConfig();
  const testMutation = useTestSSOConnection();

  const [seeded, setSeeded] = useState(false);
  const [issuer, setIssuer] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [secretSet, setSecretSet] = useState(false);
  // The (issuer, clientId) pair the most recent PASSING test covered, or null
  // if none has passed yet this editing session. Seeded from the loaded
  // config when it was already saved enabled — an admin revisiting an
  // already-live config isn't forced to re-test before touching anything
  // else, but editing either field invalidates it again below.
  const [testedPair, setTestedPair] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<SSOTestConnectionResult | null>(null);

  useEffect(() => {
    if (!data || seeded) return;
    setIssuer(data.issuer);
    setClientId(data.clientId);
    setEnabled(data.enabled);
    setSecretSet(data.secretSet);
    if (data.configured && data.enabled) setTestedPair(pairKey(data.issuer, data.clientId));
    setSeeded(true);
  }, [data, seeded]);

  const canEnable = testedPair !== null && testedPair === pairKey(issuer, clientId);

  // Editing the issuer/client-id away from the last-passing pair revokes
  // Enable — visibly (unchecked), not a silent downgrade at save time.
  useEffect(() => {
    if (!canEnable && enabled) setEnabled(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEnable]);

  if (isPending) return <LoadingState label="Loading SSO configuration…" />;
  if (isError) {
    if (isUnauthorized(error)) return <LoadingState label="Loading SSO configuration…" />;
    return (
      <ErrorState
        description={error instanceof Error ? error.message : "Couldn't load the SSO configuration. Check your connection and try again."}
        onRetry={refetch}
      />
    );
  }
  if (!data) return null;

  const issuerValid = issuer.trim() !== "" && isHttpsUrl(issuer.trim());
  const clientIdValid = clientId.trim() !== "";
  const canSubmit = issuerValid && clientIdValid && !updateMutation.isPending;

  function runTest() {
    setTestResult(null);
    testMutation.mutate(
      { issuer: issuer.trim(), clientId: clientId.trim() },
      {
        onSuccess: (result) => {
          setTestResult(result);
          if (result.ok) setTestedPair(pairKey(issuer, clientId));
        },
        onError: (err) => {
          setTestResult({ ok: false, message: err instanceof Error ? err.message : "Test failed. Try again." });
        },
      },
    );
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!issuerValid || !clientIdValid) return;
    updateMutation.mutate(
      { issuer: issuer.trim(), clientId: clientId.trim(), enabled: enabled && canEnable, clientSecret },
      {
        onSuccess: (saved) => {
          setSecretSet(saved.secretSet);
          setClientSecret("");
          pushToast({ title: "SSO configuration saved.", tone: "success" });
        },
        onError: (err) => {
          pushToast({
            title: "Couldn't save the SSO configuration",
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  return (
    <>
      <section className="sso-config-section" aria-labelledby="sso-provider-heading">
        <h2 id="sso-provider-heading" className="sso-config-section__title">
          Provider
        </h2>
        <p className="sso-config-section__note">
          One identity provider per workspace. Users sign in through it after an admin invites them (see Users) — SSO
          never auto-creates an account.
        </p>

        <form className="sso-config-form" onSubmit={handleSubmit} noValidate>
          <label className="form-field" htmlFor={issuerId}>
            <span>Issuer URL</span>
            <input
              id={issuerId}
              type="url"
              placeholder="https://idp.example.com"
              autoComplete="off"
              spellCheck={false}
              value={issuer}
              onChange={(e) => setIssuer(e.target.value)}
              required
            />
            <span className="form-field__hint">
              Your identity provider's OIDC issuer. Must be https — discovery and token verification both depend on it.
            </span>
            {issuer.trim() !== "" && !issuerValid && (
              <span className="form-field__error" role="alert">
                Enter a valid https URL.
              </span>
            )}
          </label>

          <label className="form-field" htmlFor={clientIdId}>
            <span>Client ID</span>
            <input
              id={clientIdId}
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              required
            />
          </label>

          <label className="form-field" htmlFor={clientSecretId}>
            <span>{secretSet ? "New client secret" : "Client secret"}</span>
            <input
              id={clientSecretId}
              type="password"
              autoComplete="new-password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
            />
            <span className="form-field__hint">
              {secretSet
                ? "A secret is set. Write-only — it is never shown here again. Leave blank to keep it; type a new value to replace it."
                : "Write-only — sealed on save and never shown again, including to admins."}
            </span>
          </label>

          <div className="sso-config__callback">
            <span className="form-field__label">Callback URL</span>
            <div className="sso-config__callback-row">
              <input type="text" readOnly value={data.callbackUrl} aria-label="Callback URL" />
              <CopyButton getValue={() => data.callbackUrl} label="Copy" />
            </div>
            <span className="form-field__hint">Register this as the redirect URI at your identity provider.</span>
          </div>

          <div className="sso-config__test">
            <Button type="button" variant="secondary" onClick={runTest} disabled={!issuerValid || !clientIdValid || testMutation.isPending}>
              {testMutation.isPending ? "Testing…" : "Test connection"}
            </Button>
            {testResult && (
              <p
                className={`sso-config__test-result sso-config__test-result--${testResult.ok ? "ok" : "fail"}`}
                role={testResult.ok ? "status" : "alert"}
              >
                {testResult.message}
              </p>
            )}
          </div>

          <label className="form-field form-field--checkbox" htmlFor={enabledId}>
            <span className="form-field--checkbox__row">
              <input
                id={enabledId}
                type="checkbox"
                checked={enabled}
                disabled={!canEnable}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              <span>Enabled</span>
            </span>
            <span className="form-field__hint">
              {canEnable
                ? "Users can sign in with SSO once this is saved enabled."
                : "Run a passing “Test connection” against the issuer and client ID above before you can enable sign-in."}
            </span>
          </label>

          <div className="form-actions">
            <Button type="submit" variant="primary" disabled={!canSubmit}>
              {updateMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      </section>

      <section className="sso-config-section" aria-labelledby="sso-mapping-heading">
        <h2 id="sso-mapping-heading" className="sso-config-section__title">
          Group → role mapping
        </h2>
        <p className="sso-config-section__note">
          Not available yet. Every invited SSO user is created as Viewer; raise their role manually from Users after
          their first sign-in. Mapping identity-provider groups to roles automatically is a planned fast-follow
          (ADR-0037 §6).
        </p>
      </section>
    </>
  );
}

export function SSOConfigPage() {
  return (
    <AdminGate>
      <PageHeader
        title="Single sign-on"
        description="Let your team sign in through your organization's identity provider (OIDC)."
      />
      <SSOConfigForm />
    </AdminGate>
  );
}
